/**
 * Agrupa a serie diaria de comissoes em dia, semana ou mes.
 *
 * Decisoes nao obvias:
 *  1. Ate 14 dias de janela, uma barra por dia; ate 14 semanas (98 dias),
 *     uma por semana (segunda a domingo); acima disso, e em "desde o inicio"
 *     (janela nula), uma por mes. Nao ha seletor: a granularidade segue a
 *     janela do cabecalho, e o titulo do grafico diz qual e.
 *  2. Toda conta de data e em UTC sobre `YYYY-MM-DD`: o dia ja veio fechado
 *     pelo servidor, e `new Date('2026-09-24')` num fuso com horario de verao
 *     poderia cair no dia anterior.
 *  3. Dinheiro e somado em centavos inteiros; nada e dividido aqui.
 */

import type { CommissionSeriesPoint } from './types';
import { formatMes } from './vocabulario';

export type Granularidade = 'dia' | 'semana' | 'mes';

export const LIMITE_DIAS_POR_DIA = 14;
export const LIMITE_DIAS_POR_SEMANA = 98;
const DIA_MS = 86_400_000;

export interface Balde {
  /** Chave ordenavel: o dia, a segunda-feira da semana (YYYY-MM-DD) ou o mes (YYYY-MM). */
  readonly chave: string;
  /** Rotulo curto do eixo X: "24/09", "21/09–27/09" ou "set/2026". */
  readonly rotulo: string;
  readonly recuperadoCents: number;
  readonly comissaoCents: number;
}

export interface SerieAgrupada {
  readonly granularidade: Granularidade;
  readonly baldes: readonly Balde[];
  readonly labels: readonly string[];
  readonly recuperadoCents: readonly number[];
  readonly comissaoCents: readonly number[];
}

/** `YYYY-MM-DD` -> meia-noite UTC. Entrada invalida vira a epoca, nunca NaN. */
function dataUtc(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

function somarDias(date: Date, dias: number): Date {
  return new Date(date.getTime() + dias * DIA_MS);
}

/** Segunda-feira da semana do dia (0 = segunda ... 6 = domingo). */
function segundaDaSemana(date: Date): Date {
  return somarDias(date, -((date.getUTCDay() + 6) % 7));
}

function yyyymmdd(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function ddmm(date: Date): string {
  const d = String(date.getUTCDate()).padStart(2, '0');
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${d}/${m}`;
}

export function granularidadeDaJanela(dias: number | null): Granularidade {
  if (dias === null) {
    return 'mes';
  }
  if (dias <= LIMITE_DIAS_POR_DIA) {
    return 'dia';
  }
  return dias <= LIMITE_DIAS_POR_SEMANA ? 'semana' : 'mes';
}

export function chaveDoBalde(day: string, granularidade: Granularidade): string {
  switch (granularidade) {
    case 'dia':
      return day.slice(0, 10);
    case 'semana':
      return yyyymmdd(segundaDaSemana(dataUtc(day)));
    case 'mes':
      return day.slice(0, 7);
  }
}

export function rotuloDoBalde(chave: string, granularidade: Granularidade): string {
  switch (granularidade) {
    case 'dia':
      return ddmm(dataUtc(chave));
    case 'semana': {
      const inicio = dataUtc(chave);
      return `${ddmm(inicio)}–${ddmm(somarDias(inicio, 6))}`;
    }
    case 'mes':
      return formatMes(chave);
  }
}

export function agruparSerie(points: readonly CommissionSeriesPoint[], granularidade: Granularidade): SerieAgrupada {
  const baldes = new Map<string, Balde>();
  for (const p of points) {
    const chave = chaveDoBalde(p.day, granularidade);
    const atual = baldes.get(chave) ?? { chave, rotulo: rotuloDoBalde(chave, granularidade), recuperadoCents: 0, comissaoCents: 0 };
    baldes.set(chave, {
      ...atual,
      recuperadoCents: atual.recuperadoCents + p.recoveredAmountCents,
      comissaoCents: atual.comissaoCents + p.commissionCents,
    });
  }
  // Chaves YYYY-MM(-DD) ordenam como texto; a ordem cronologica nao depende de a serie ter chegado ordenada.
  const lista = [...baldes.values()].sort((a, b) => (a.chave < b.chave ? -1 : a.chave > b.chave ? 1 : 0));
  return {
    granularidade,
    baldes: lista,
    labels: lista.map((b) => b.rotulo),
    recuperadoCents: lista.map((b) => b.recuperadoCents),
    comissaoCents: lista.map((b) => b.comissaoCents),
  };
}
